import { useState, useMemo, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Download, AlertTriangle, FileText,
  ChevronLeft, ChevronRight, Filter,
} from 'lucide-react';
import { useThemeStore } from '../../stores/theme.store';
import {
  useDetailedTransactionsReport,
  useExportReport,
} from '../../hooks/useFinancialReports';
import type {
  DetailedTransactionsFilters,
  TransactionEntry,
  GroupTotalEntry,
} from '../../hooks/useFinancialReports';

// ── helpers ──────────────────────────────────────────────

function fmtBRL(centavos: number | undefined | null): string {
  if (centavos === null || centavos === undefined) return 'R$ 0,00';
  return `R$ ${(centavos / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function getDefaultDates() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  return { startDate: start.toISOString().split('T')[0], endDate: now.toISOString().split('T')[0] };
}

const TYPE_LABELS: Record<string, string> = {
  PIX_RECEIVE: 'PIX Recebido',
  PIX_SEND: 'PIX Enviado',
  TRANSFER_INTERNAL: 'Transferência',
  BOLETO: 'Boleto',
  WITHDRAWAL: 'Saque',
};

const STATUS_CFG: Record<string, { label: string; cls: string }> = {
  COMPLETED: { label: 'Concluída', cls: 'bg-green-100 text-green-700' },
  PENDING: { label: 'Pendente', cls: 'bg-yellow-100 text-yellow-700' },
  FAILED: { label: 'Falha', cls: 'bg-red-100 text-red-700' },
  CANCELLED: { label: 'Cancelada', cls: 'bg-gray-100 text-gray-700' },
};

const ALL_TYPES = ['PIX_RECEIVE', 'PIX_SEND', 'TRANSFER_INTERNAL', 'BOLETO', 'WITHDRAWAL'];
const ALL_STATUSES = ['COMPLETED', 'PENDING', 'FAILED', 'CANCELLED'];

// ── Multi-select dropdown ────────────────────────────────

function MultiSelectDropdown({
  label, options, selected, onChange, labelMap, cardBg, textPrimary, textSecondary, border, inputBg,
}: {
  label: string;
  options: string[];
  selected: string[];
  onChange: (v: string[]) => void;
  labelMap: Record<string, string>;
  cardBg: string;
  textPrimary: string;
  textSecondary: string;
  border: string;
  inputBg: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggle = (val: string) => {
    onChange(selected.includes(val) ? selected.filter((s) => s !== val) : [...selected, val]);
  };

  const display = selected.length === 0 ? 'Todos' : selected.length === 1 ? (labelMap[selected[0]] || selected[0]) : `${selected.length} selecionados`;

  return (
    <div ref={ref} className="relative">
      <label className={`block text-xs font-medium mb-1 ${textSecondary}`}>{label}</label>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`flex items-center justify-between gap-2 rounded-lg border px-3 py-2 text-sm min-w-[160px] ${inputBg}`}
      >
        <span className="truncate">{display}</span>
        <Filter className="w-3 h-3 shrink-0 opacity-60" />
      </button>
      {open && (
        <div className={`absolute z-20 mt-1 w-56 rounded-lg shadow-lg border ${border} ${cardBg} max-h-60 overflow-y-auto`}>
          {options.map((opt) => (
            <label key={opt} className={`flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-blue-500/10 ${textPrimary}`}>
              <input type="checkbox" checked={selected.includes(opt)} onChange={() => toggle(opt)} className="rounded" />
              {labelMap[opt] || opt}
            </label>
          ))}
          {selected.length > 0 && (
            <button onClick={() => onChange([])} className={`w-full text-left px-3 py-2 text-xs ${textSecondary} hover:bg-blue-500/10 border-t ${border}`}>
              Limpar seleção
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main Component ───────────────────────────────────────

export function DetailedTransactionsReportPage() {
  const navigate = useNavigate();
  const { isDarkMode } = useThemeStore();
  const defaults = getDefaultDates();

  // Filter state
  const [startDate, setStartDate] = useState(defaults.startDate);
  const [endDate, setEndDate] = useState(defaults.endDate);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [virtualAccountId, setVirtualAccountId] = useState('');
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');
  const [groupBy, setGroupBy] = useState<string>('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [showExportMenu, setShowExportMenu] = useState(false);

  const exportMutation = useExportReport();

  const filters: DetailedTransactionsFilters = useMemo(() => ({
    startDate,
    endDate,
    ...(selectedTypes.length > 0 ? { type: selectedTypes } : {}),
    ...(selectedStatuses.length > 0 ? { status: selectedStatuses } : {}),
    ...(virtualAccountId ? { virtualAccountId } : {}),
    ...(minAmount ? { minAmount: Math.round(parseFloat(minAmount) * 100) } : {}),
    ...(maxAmount ? { maxAmount: Math.round(parseFloat(maxAmount) * 100) } : {}),
    ...(groupBy ? { groupBy: groupBy as DetailedTransactionsFilters['groupBy'] } : {}),
    page,
    limit,
  }), [startDate, endDate, selectedTypes, selectedStatuses, virtualAccountId, minAmount, maxAmount, groupBy, page, limit]);

  const { data, isLoading } = useDetailedTransactionsReport(filters);

  // Theme
  const bg = isDarkMode ? 'bg-gray-900' : 'bg-gray-50';
  const cardBg = isDarkMode ? 'bg-gray-800' : 'bg-white';
  const textPrimary = isDarkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  const border = isDarkMode ? 'border-gray-700' : 'border-gray-200';
  const inputBg = isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300';

  const handleExport = (format: 'PDF' | 'CSV' | 'XLSX') => {
    setShowExportMenu(false);
    const exportFilters = { ...filters };
    delete (exportFilters as any).page;
    delete (exportFilters as any).limit;
    exportMutation.mutate({ reportType: 'detailed-transactions', format, filters: exportFilters });
  };

  const anomalySummary = data?.anomalySummary;
  const transactions = data?.transactions ?? [];
  const groupTotals = data?.groupTotals;
  const pagination = data?.pagination;

  // ── Skeleton ─────────────────────────────────────────
  if (isLoading) {
    return (
      <div className={`min-h-screen ${bg} p-6`}>
        <div className="max-w-7xl mx-auto space-y-6">
          <div className="h-8 w-72 rounded bg-gray-300 dark:bg-gray-700 animate-pulse" />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className={`${cardBg} rounded-xl p-5 space-y-3`}>
                <div className="h-4 w-24 rounded bg-gray-300 dark:bg-gray-700 animate-pulse" />
                <div className="h-7 w-36 rounded bg-gray-300 dark:bg-gray-700 animate-pulse" />
              </div>
            ))}
          </div>
          <div className={`${cardBg} rounded-xl p-6 h-96 animate-pulse`} />
        </div>
      </div>
    );
  }

  // ── Empty state ──────────────────────────────────────
  if (!data || transactions.length === 0) {
    return (
      <div className={`min-h-screen ${bg} p-6`}>
        <div className="max-w-7xl mx-auto">
          <button onClick={() => navigate('/admin/reports')} className={`flex items-center gap-2 mb-6 ${textSecondary} hover:opacity-80 transition-opacity`}>
            <ArrowLeft className="w-4 h-4" /> Voltar
          </button>
          <div className={`${cardBg} rounded-xl p-12 text-center`}>
            <FileText className={`w-12 h-12 mx-auto mb-4 ${textSecondary}`} />
            <h2 className={`text-lg font-semibold ${textPrimary}`}>Nenhuma transação encontrada</h2>
            <p className={`mt-2 ${textSecondary}`}>Não há transações para os filtros selecionados. Ajuste os filtros e tente novamente.</p>
          </div>
        </div>
      </div>
    );
  }

  // ── Anomaly summary cards ────────────────────────────
  const anomalyCards = [
    {
      label: 'Anomalias Detectadas',
      value: String(anomalySummary?.totalAnomalies ?? 0),
      color: (anomalySummary?.totalAnomalies ?? 0) > 0 ? 'text-orange-500' : 'text-green-500',
      iconBg: (anomalySummary?.totalAnomalies ?? 0) > 0 ? 'bg-orange-500/10' : 'bg-green-500/10',
    },
    {
      label: 'Valor Total Anomalias',
      value: fmtBRL(anomalySummary?.totalAnomalyValue),
      color: (anomalySummary?.totalAnomalyValue ?? 0) > 0 ? 'text-orange-500' : 'text-green-500',
      iconBg: (anomalySummary?.totalAnomalyValue ?? 0) > 0 ? 'bg-orange-500/10' : 'bg-green-500/10',
    },
    {
      label: 'Tipos de Anomalia',
      value: (anomalySummary?.anomalyTypes ?? []).length > 0
        ? anomalySummary!.anomalyTypes.map((t) => t === 'HIGH_VALUE' ? 'Alto Valor' : t === 'UNUSUAL_TIME' ? 'Horário Atípico' : t).join(', ')
        : 'Nenhuma',
      color: (anomalySummary?.anomalyTypes ?? []).length > 0 ? 'text-orange-500' : 'text-green-500',
      iconBg: (anomalySummary?.anomalyTypes ?? []).length > 0 ? 'bg-orange-500/10' : 'bg-green-500/10',
    },
  ];

  // ── Build grouped rows ───────────────────────────────
  const buildRows = (): (TransactionEntry | GroupTotalEntry)[] => {
    if (!groupBy || !groupTotals || groupTotals.length === 0) return transactions;

    const rows: (TransactionEntry | GroupTotalEntry)[] = [];
    const grouped = new Map<string, TransactionEntry[]>();

    for (const tx of transactions) {
      let key = '';
      if (groupBy === 'type') key = tx.type;
      else if (groupBy === 'status') key = tx.status;
      else if (groupBy === 'date') key = tx.createdAt.split('T')[0];
      else if (groupBy === 'account') key = tx.payerAccountId || tx.receiverAccountId || 'unknown';
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key)!.push(tx);
    }

    for (const gt of groupTotals) {
      rows.push(gt);
      const txs = grouped.get(gt.groupValue) ?? [];
      rows.push(...txs);
    }

    return rows;
  };

  const displayRows = buildRows();

  const isGroupTotal = (row: TransactionEntry | GroupTotalEntry): row is GroupTotalEntry =>
    'groupKey' in row && 'transactionCount' in row && !('id' in row);

  return (
    <div className={`min-h-screen ${bg} p-6`}>
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/admin/reports')} className={`p-2 rounded-lg ${cardBg} border ${border} hover:opacity-80 transition-opacity`}>
              <ArrowLeft className={`w-4 h-4 ${textPrimary}`} />
            </button>
            <div>
              <h1 className={`text-2xl font-bold ${textPrimary}`}>Transações Detalhadas</h1>
              <p className={`text-sm ${textSecondary}`}>Relatório completo com filtros avançados e detecção de anomalias</p>
            </div>
          </div>
          {/* Export */}
          <div className="relative">
            <button
              onClick={() => setShowExportMenu(!showExportMenu)}
              disabled={exportMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
            >
              <Download className="w-4 h-4" />
              {exportMutation.isPending ? 'Exportando...' : 'Exportar'}
            </button>
            {showExportMenu && (
              <div className={`absolute right-0 mt-2 w-40 rounded-lg shadow-lg border ${border} ${cardBg} z-10`}>
                {(['PDF', 'CSV', 'XLSX'] as const).map((fmt) => (
                  <button key={fmt} onClick={() => handleExport(fmt)} className={`block w-full text-left px-4 py-2 text-sm ${textPrimary} hover:bg-blue-500/10 first:rounded-t-lg last:rounded-b-lg`}>
                    {fmt === 'XLSX' ? 'Excel' : fmt}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Anomaly Summary */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {anomalyCards.map((card) => (
            <div key={card.label} className={`${cardBg} rounded-xl p-5 border ${border}`}>
              <div className="flex items-center gap-3 mb-2">
                <div className={`p-2 rounded-lg ${card.iconBg}`}>
                  <AlertTriangle className={`w-5 h-5 ${card.color}`} />
                </div>
                <span className={`text-sm font-medium ${textSecondary}`}>{card.label}</span>
              </div>
              <p className={`text-xl font-bold ${card.color}`}>{card.value}</p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className={`${cardBg} rounded-xl p-4 border ${border}`}>
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label className={`block text-xs font-medium mb-1 ${textSecondary}`}>Data Início</label>
              <input type="date" value={startDate} onChange={(e) => { setStartDate(e.target.value); setPage(1); }} className={`rounded-lg border px-3 py-2 text-sm ${inputBg}`} />
            </div>
            <div>
              <label className={`block text-xs font-medium mb-1 ${textSecondary}`}>Data Fim</label>
              <input type="date" value={endDate} onChange={(e) => { setEndDate(e.target.value); setPage(1); }} className={`rounded-lg border px-3 py-2 text-sm ${inputBg}`} />
            </div>
            <MultiSelectDropdown
              label="Tipo"
              options={ALL_TYPES}
              selected={selectedTypes}
              onChange={(v) => { setSelectedTypes(v); setPage(1); }}
              labelMap={TYPE_LABELS}
              cardBg={cardBg}
              textPrimary={textPrimary}
              textSecondary={textSecondary}
              border={border}
              inputBg={inputBg}
            />
            <MultiSelectDropdown
              label="Status"
              options={ALL_STATUSES}
              selected={selectedStatuses}
              onChange={(v) => { setSelectedStatuses(v); setPage(1); }}
              labelMap={Object.fromEntries(ALL_STATUSES.map((s) => [s, STATUS_CFG[s]?.label ?? s]))}
              cardBg={cardBg}
              textPrimary={textPrimary}
              textSecondary={textSecondary}
              border={border}
              inputBg={inputBg}
            />
            <div>
              <label className={`block text-xs font-medium mb-1 ${textSecondary}`}>Conta Virtual</label>
              <input
                type="text"
                value={virtualAccountId}
                onChange={(e) => { setVirtualAccountId(e.target.value); setPage(1); }}
                placeholder="ID da conta..."
                className={`rounded-lg border px-3 py-2 text-sm w-40 ${inputBg}`}
              />
            </div>
            <div>
              <label className={`block text-xs font-medium mb-1 ${textSecondary}`}>Valor Mín (R$)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={minAmount}
                onChange={(e) => { setMinAmount(e.target.value); setPage(1); }}
                placeholder="0,00"
                className={`rounded-lg border px-3 py-2 text-sm w-28 ${inputBg}`}
              />
            </div>
            <div>
              <label className={`block text-xs font-medium mb-1 ${textSecondary}`}>Valor Máx (R$)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={maxAmount}
                onChange={(e) => { setMaxAmount(e.target.value); setPage(1); }}
                placeholder="0,00"
                className={`rounded-lg border px-3 py-2 text-sm w-28 ${inputBg}`}
              />
            </div>
            <div>
              <label className={`block text-xs font-medium mb-1 ${textSecondary}`}>Agrupar por</label>
              <select value={groupBy} onChange={(e) => { setGroupBy(e.target.value); setPage(1); }} className={`rounded-lg border px-3 py-2 text-sm ${inputBg}`}>
                <option value="">Sem agrupamento</option>
                <option value="type">Tipo</option>
                <option value="status">Status</option>
                <option value="date">Data</option>
                <option value="account">Conta</option>
              </select>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className={`${cardBg} rounded-xl border ${border} overflow-hidden`}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className={`border-b ${border} ${isDarkMode ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
                  <th className={`px-4 py-3 text-left font-medium ${textSecondary}`}>Data/Hora</th>
                  <th className={`px-4 py-3 text-left font-medium ${textSecondary}`}>Tipo</th>
                  <th className={`px-4 py-3 text-left font-medium ${textSecondary}`}>Status</th>
                  <th className={`px-4 py-3 text-right font-medium ${textSecondary}`}>Valor Bruto</th>
                  <th className={`px-4 py-3 text-right font-medium ${textSecondary}`}>Taxas</th>
                  <th className={`px-4 py-3 text-right font-medium ${textSecondary}`}>Valor Líquido</th>
                  <th className={`px-4 py-3 text-left font-medium ${textSecondary}`}>Pagador</th>
                  <th className={`px-4 py-3 text-left font-medium ${textSecondary}`}>Recebedor</th>
                  <th className={`px-4 py-3 text-left font-medium ${textSecondary}`}>Correlation ID</th>
                  <th className={`px-4 py-3 text-center font-medium ${textSecondary}`}>Anomalia</th>
                </tr>
              </thead>
              <tbody>
                {displayRows.map((row, idx) => {
                  if (isGroupTotal(row)) {
                    // Subtotal row
                    return (
                      <tr key={`group-${row.groupKey}-${row.groupValue}`} className={`border-b ${border} ${isDarkMode ? 'bg-blue-900/20' : 'bg-blue-50'} font-semibold`}>
                        <td colSpan={3} className={`px-4 py-3 ${textPrimary}`}>
                          {row.groupKey === 'type' ? (TYPE_LABELS[row.groupValue] || row.groupValue) : row.groupValue}
                          <span className={`ml-2 text-xs font-normal ${textSecondary}`}>({row.transactionCount} transações)</span>
                        </td>
                        <td className={`px-4 py-3 text-right ${textPrimary}`}>{fmtBRL(row.totalGrossAmount)}</td>
                        <td className={`px-4 py-3 text-right ${textPrimary}`}>{fmtBRL(row.totalFees)}</td>
                        <td className={`px-4 py-3 text-right ${textPrimary}`}>{fmtBRL(row.totalNetAmount)}</td>
                        <td colSpan={4} />
                      </tr>
                    );
                  }

                  const tx = row as TransactionEntry;
                  const anomalyRowBg = tx.isAnomaly
                    ? isDarkMode ? 'bg-yellow-900/20 border-l-4 border-l-yellow-500' : 'bg-yellow-50 border-l-4 border-l-yellow-500'
                    : '';
                  const statusCfg = STATUS_CFG[tx.status] ?? { label: tx.status, cls: 'bg-gray-100 text-gray-700' };

                  return (
                    <tr key={tx.id || idx} className={`border-b ${border} ${anomalyRowBg} hover:${isDarkMode ? 'bg-gray-700/30' : 'bg-gray-50'} transition-colors`}>
                      <td className={`px-4 py-3 ${textPrimary} whitespace-nowrap`}>{fmtDate(tx.createdAt)}</td>
                      <td className={`px-4 py-3 ${textPrimary}`}>{TYPE_LABELS[tx.type] || tx.type}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusCfg.cls}`}>
                          {statusCfg.label}
                        </span>
                      </td>
                      <td className={`px-4 py-3 text-right ${textPrimary}`}>{fmtBRL(tx.grossAmount)}</td>
                      <td className={`px-4 py-3 text-right ${textSecondary}`}>{fmtBRL(tx.totalFees)}</td>
                      <td className={`px-4 py-3 text-right ${textPrimary}`}>{fmtBRL(tx.netAmount)}</td>
                      <td className={`px-4 py-3 ${textSecondary} truncate max-w-[120px]`} title={tx.payerName || '—'}>{tx.payerName || '—'}</td>
                      <td className={`px-4 py-3 ${textSecondary} truncate max-w-[120px]`} title={tx.receiverName || '—'}>{tx.receiverName || '—'}</td>
                      <td className={`px-4 py-3 font-mono text-xs ${textSecondary} truncate max-w-[100px]`} title={tx.correlationId}>{tx.correlationId ? tx.correlationId.slice(0, 8) + '...' : '—'}</td>
                      <td className="px-4 py-3 text-center">
                        {tx.isAnomaly ? (
                          <span className="inline-flex items-center gap-1 text-orange-500" title={tx.anomalyType === 'HIGH_VALUE' ? 'Alto Valor' : tx.anomalyType === 'UNUSUAL_TIME' ? 'Horário Atípico' : tx.anomalyType || ''}>
                            <AlertTriangle className="w-4 h-4" />
                            <span className="text-xs font-medium">{tx.anomalyType === 'HIGH_VALUE' ? 'Alto Valor' : tx.anomalyType === 'UNUSUAL_TIME' ? 'Horário' : tx.anomalyType}</span>
                          </span>
                        ) : (
                          <span className={`text-xs ${textSecondary}`}>—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination && (
            <div className={`flex flex-col sm:flex-row items-center justify-between gap-4 px-4 py-3 border-t ${border}`}>
              <div className="flex items-center gap-2">
                <span className={`text-sm ${textSecondary}`}>Itens por página:</span>
                <select
                  value={limit}
                  onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
                  className={`rounded-lg border px-2 py-1 text-sm ${inputBg}`}
                >
                  {[10, 25, 50, 100].map((v) => (
                    <option key={v} value={v}>{v}</option>
                  ))}
                </select>
                <span className={`text-sm ${textSecondary}`}>
                  {((pagination.page - 1) * pagination.limit) + 1}–{Math.min(pagination.page * pagination.limit, pagination.total)} de {pagination.total}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page <= 1}
                  className={`p-2 rounded-lg border ${border} ${cardBg} disabled:opacity-40 hover:opacity-80 transition-opacity`}
                >
                  <ChevronLeft className={`w-4 h-4 ${textPrimary}`} />
                </button>
                <span className={`text-sm ${textPrimary}`}>
                  Página {pagination.page} de {pagination.pages}
                </span>
                <button
                  onClick={() => setPage(Math.min(pagination.pages, page + 1))}
                  disabled={page >= pagination.pages}
                  className={`p-2 rounded-lg border ${border} ${cardBg} disabled:opacity-40 hover:opacity-80 transition-opacity`}
                >
                  <ChevronRight className={`w-4 h-4 ${textPrimary}`} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
